import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { OnboardingListConfig, OnboardingListTypeKey } from "./onboardingListTypes";
import { ConfigFieldErrors } from "./onboardingListValidation";
import { OnboardingListConfigForm } from "./OnboardingListConfigs";
import { OnboardingFilterOptions } from "./configs/useOnboardingFilterOptions";

/**
 * One list type in the onboarding picker: title row with a select indicator,
 * expanding into the type's compact config form while selected. `errors` is
 * empty until the user tries to continue, so the card only turns red once the
 * missing input actually blocks them.
 */
export const OnboardingListRow: FunctionComponent<{
	typeKey: OnboardingListTypeKey;
	config: OnboardingListConfig | null;
	options: OnboardingFilterOptions;
	errors?: ConfigFieldErrors;
	onToggle: () => void;
	onConfigChange: (config: OnboardingListConfig) => void;
}> = ({ typeKey, config, options, errors, onToggle, onConfigChange }) => {
	const { t } = useTranslation();
	const isSelected = config !== null;
	const fieldErrors = errors ?? {};
	const hasErrors = Object.keys(fieldErrors).length > 0;

	return (
		<div
			className={classNames(
				"mb-2 rounded-xl border transition-colors duration-200",
				hasErrors
					? "border-status-error bg-status-error-light/40"
					: isSelected
						? "border-brand-light bg-blue-action/5"
						: "border-gray-200"
			)}
		>
			<button
				className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left"
				type="button"
				onClick={onToggle}
			>
				<span className="min-w-0 flex-1">
					<span className="block text-ssm font-semibold">
						{t(`onboarding.lists.types.${typeKey}.title`)}
					</span>
					<span className="block truncate text-xs text-text-meta">
						{t(`onboarding.lists.types.${typeKey}.description`)}
					</span>
				</span>
				<span
					className={classNames(
						"flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border text-[11px] font-bold",
						isSelected
							? "border-brand-light bg-blue-action text-white"
							: "border-gray-200 text-transparent"
					)}
				>
					✓
				</span>
			</button>

			{isSelected && (
				<div className="border-t border-gray-200 px-4 py-3">
					<OnboardingListConfigForm
						errors={fieldErrors}
						options={options}
						value={config}
						onChange={onConfigChange}
					/>
				</div>
			)}
		</div>
	);
};
